export enum BlockType {
  AIR = 0,
  STONE = 1,
  DIRT = 2,
  GRASS = 3,
  SAND = 4,
  WATER = 5,
  WOOD = 6,
  LEAVES = 7,
  BEDROCK = 8,
  COBBLESTONE = 9,
  PLANKS = 10,
  GLASS = 11,
  COAL_ORE = 12,
  IRON_ORE = 13,
  GOLD_ORE = 14,
  DIAMOND_ORE = 15,
  CRAFTING_TABLE = 16,
  BED = 17,
  CHEST = 18,
  FURNACE = 19,
}

export type BlockProperties = {
  id: BlockType;
  name: string;
  solid: boolean;
  transparent: boolean;
  breakTime: number; // seconds (0 = unbreakable)
  /** UV coords in texture atlas [top, side, bottom] — row,col in 16x16 grid */
  textureUV: [top: [number, number], side: [number, number], bottom: [number, number]];
};

export const BLOCK_REGISTRY: Record<BlockType, BlockProperties> = {
  [BlockType.AIR]: {
    id: BlockType.AIR,
    name: "air",
    solid: false,
    transparent: true,
    breakTime: 0,
    textureUV: [[0, 0], [0, 0], [0, 0]],
  },
  [BlockType.STONE]: {
    id: BlockType.STONE,
    name: "stone",
    solid: true,
    transparent: false,
    breakTime: 1.5,
    textureUV: [[1, 0], [1, 0], [1, 0]],
  },
  [BlockType.DIRT]: {
    id: BlockType.DIRT,
    name: "dirt",
    solid: true,
    transparent: false,
    breakTime: 0.5,
    textureUV: [[2, 0], [2, 0], [2, 0]],
  },
  [BlockType.GRASS]: {
    id: BlockType.GRASS,
    name: "grass",
    solid: true,
    transparent: false,
    breakTime: 0.6,
    textureUV: [[0, 1], [3, 0], [2, 0]], // top=grass_top, side=grass_side, bottom=dirt
  },
  [BlockType.SAND]: {
    id: BlockType.SAND,
    name: "sand",
    solid: true,
    transparent: false,
    breakTime: 0.5,
    textureUV: [[4, 0], [4, 0], [4, 0]],
  },
  [BlockType.WATER]: {
    id: BlockType.WATER,
    name: "water",
    solid: false,
    transparent: true,
    breakTime: 0,
    textureUV: [[5, 0], [5, 0], [5, 0]],
  },
  [BlockType.WOOD]: {
    id: BlockType.WOOD,
    name: "wood",
    solid: true,
    transparent: false,
    breakTime: 2,
    textureUV: [[6, 1], [6, 0], [6, 1]], // top=rings, side=bark, bottom=rings
  },
  [BlockType.LEAVES]: {
    id: BlockType.LEAVES,
    name: "leaves",
    solid: true,
    transparent: true,
    breakTime: 0.2,
    textureUV: [[7, 0], [7, 0], [7, 0]],
  },
  [BlockType.BEDROCK]: {
    id: BlockType.BEDROCK,
    name: "bedrock",
    solid: true,
    transparent: false,
    breakTime: 0, // unbreakable
    textureUV: [[8, 0], [8, 0], [8, 0]],
  },
  [BlockType.COBBLESTONE]: {
    id: BlockType.COBBLESTONE,
    name: "cobblestone",
    solid: true,
    transparent: false,
    breakTime: 2,
    textureUV: [[9, 0], [9, 0], [9, 0]],
  },
  [BlockType.PLANKS]: {
    id: BlockType.PLANKS,
    name: "planks",
    solid: true,
    transparent: false,
    breakTime: 2,
    textureUV: [[10, 0], [10, 0], [10, 0]],
  },
  [BlockType.GLASS]: {
    id: BlockType.GLASS,
    name: "glass",
    solid: true,
    transparent: true,
    breakTime: 0.3,
    textureUV: [[11, 0], [11, 0], [11, 0]],
  },
  [BlockType.COAL_ORE]: {
    id: BlockType.COAL_ORE,
    name: "coal_ore",
    solid: true,
    transparent: false,
    breakTime: 3,
    textureUV: [[12, 0], [12, 0], [12, 0]],
  },
  [BlockType.IRON_ORE]: {
    id: BlockType.IRON_ORE,
    name: "iron_ore",
    solid: true,
    transparent: false,
    breakTime: 3,
    textureUV: [[13, 0], [13, 0], [13, 0]],
  },
  [BlockType.GOLD_ORE]: {
    id: BlockType.GOLD_ORE,
    name: "gold_ore",
    solid: true,
    transparent: false,
    breakTime: 3,
    textureUV: [[14, 0], [14, 0], [14, 0]],
  },
  [BlockType.DIAMOND_ORE]: {
    id: BlockType.DIAMOND_ORE,
    name: "diamond_ore",
    solid: true,
    transparent: false,
    breakTime: 5,
    textureUV: [[15, 0], [15, 0], [15, 0]],
  },
  [BlockType.CRAFTING_TABLE]: {
    id: BlockType.CRAFTING_TABLE,
    name: "crafting_table",
    solid: true,
    transparent: false,
    breakTime: 2,
    textureUV: [[1, 1], [2, 1], [9, 0]],
  },
  [BlockType.BED]: {
    id: BlockType.BED,
    name: "bed",
    solid: true,
    transparent: false,
    breakTime: 1,
    textureUV: [[3, 1], [4, 1], [9, 0]],
  },
  [BlockType.CHEST]: {
    id: BlockType.CHEST,
    name: "chest",
    solid: true,
    transparent: false,
    breakTime: 2.5,
    textureUV: [[5, 1], [6, 1], [5, 1]],
  },
  [BlockType.FURNACE]: {
    id: BlockType.FURNACE,
    name: "furnace",
    solid: true,
    transparent: false,
    breakTime: 3.5,
    textureUV: [[7, 1], [8, 1], [7, 1]],
  },
};

export function isBlockSolid(blockId: number): boolean {
  const props = BLOCK_REGISTRY[blockId as BlockType];
  return props ? props.solid : false;
}

export function isBlockTransparent(blockId: number): boolean {
  const props = BLOCK_REGISTRY[blockId as BlockType];
  return props ? props.transparent : true;
}
